/**
 * autosaveStorage.ts
 *
 * Thin wrapper around idb-keyval for autosave slot management.
 * Uses a ring of 3 slots so we never overwrite the only good copy.
 */
import { get, set, del, createStore } from 'idb-keyval';

const STORE = createStore('storyball-autosave', 'slots');
const RING_SIZE = 3;
const POINTER_KEY = 'pointer'; // which slot the *next* write goes to (0-2)
const SLOT_KEY    = (n: number) => `slot-${n}`;

export interface AutosaveSlot {
  /** ISO timestamp */
  savedAt: string;
  /** project name at time of autosave */
  name: string;
  /** full serialized project JSON */
  json: string;
}

/** Write a new autosave entry, advancing the ring pointer. */
export async function writeAutosave(slot: Omit<AutosaveSlot, 'savedAt'>): Promise<void> {
  const ptr: number = (await get<number>(POINTER_KEY, STORE)) ?? 0;
  const entry: AutosaveSlot = { ...slot, savedAt: new Date().toISOString() };
  await set(SLOT_KEY(ptr), entry, STORE);
  await set(POINTER_KEY, (ptr + 1) % RING_SIZE, STORE);
}

/** Return the most-recently-written slot, or null if none exists. */
export async function readNewestAutosave(): Promise<AutosaveSlot | null> {
  const ptr: number = (await get<number>(POINTER_KEY, STORE)) ?? 0;
  // The last-written slot sits one step behind the current pointer
  for (let i = 1; i <= RING_SIZE; i++) {
    const idx = (ptr - i + RING_SIZE) % RING_SIZE;
    const slot = await get<AutosaveSlot>(SLOT_KEY(idx), STORE);
    if (slot) return slot;
  }
  return null;
}

/** Clear all autosave slots (called after a successful explicit save or new project). */
export async function clearAutosave(): Promise<void> {
  for (let i = 0; i < RING_SIZE; i++) {
    await del(SLOT_KEY(i), STORE);
  }
  await del(POINTER_KEY, STORE);
}
